import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { translations, type Language } from '../i18n/translations';
import { useAppStore } from '../stores/appStore';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    
    // Log to console
    console.error('🚨 ErrorBoundary caught:', error, errorInfo);
    
    // TODO: Send to Sentry when configured
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    // }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Get current language from store
      const language = useAppStore.getState().language as Language;
      const t = (key: keyof typeof translations.en) => translations[language][key] || translations.en[key];

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            p: 3,
            backgroundColor: '#0f172a',
            color: '#e2e8f0',
            textAlign: 'center',
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 80, color: '#ef4444', mb: 2 }} />
          
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#f87171' }}>
            {t('somethingWentWrong')}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, color: '#94a3b8', maxWidth: 400 }}>
            {t('errorDescription')}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleReload}
              sx={{
                backgroundColor: '#3b82f6',
                '&:hover': { backgroundColor: '#2563eb' },
                py: 1.5,
                px: 3,
                fontSize: '1rem',
              }}
            >
              {t('reloadApp')}
            </Button>
            
            <Button
              variant="outlined"
              onClick={this.handleReset}
              sx={{
                borderColor: '#64748b',
                color: '#94a3b8',
                py: 1.5,
                px: 3,
                '&:hover': { borderColor: '#94a3b8', backgroundColor: 'rgba(148, 163, 184, 0.1)' },
              }}
            >
              {t('tryAgain')}
            </Button>
          </Box>
          
          {/* Show error details only in dev mode */}
          {import.meta.env.DEV && this.state.error && (
            <Box
              sx={{
                mt: 4,
                p: 2,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                borderRadius: 1,
                border: '1px solid rgba(239, 68, 68, 0.3)',
                maxWidth: 600,
                overflow: 'auto',
                textAlign: 'left',
              }}
            >
              <Typography variant="caption" sx={{ color: '#f87171', fontFamily: 'monospace', fontWeight: 600 }}>
                {this.state.error.toString()}
              </Typography>
              {this.state.errorInfo && (
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{ color: '#94a3b8', fontFamily: 'monospace', mt: 1, fontSize: '0.65rem', whiteSpace: 'pre-wrap' }}
                >
                  {this.state.errorInfo.componentStack}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}
